import { apiGet, apiPost, apiCall } from './apiUtils';

/**
 * Property Service for React Website
 * Handles property operations
 */

// Helper function to get full image URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  // Remove leading slash if present to avoid double slash
  const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  return `https://sandbox.rebax.in/${cleanPath}`;
};

// Test API on import
console.log('🧪 PropertyService loaded - Testing API connection...');

const propertyService = {
  /**
   * Get all properties
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Properties data
   */
  /**
   * Get builder projects
   * @returns {Promise<Object>} List of builder projects
   */
  getBuilderProjects: async () => {
    try {
      console.log('🔍 Fetching builder projects');
      console.log('Auth Token:', localStorage.getItem('authToken'));

      const response = await apiGet('properties', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-Request-Type': 'Builder Projects'
        }
      });

      console.log('Builder Projects Response:', response);

      // Normalize output (same style as your searchProperties)
      const projects = (response?.data || []).map((item) => ({
        ...item,
        // if you need any formatting, you can add here later
      }));

      return {
        success: true,
        data: projects
      };
    } catch (error) {
      console.error('❌ Error fetching builder projects:', error);

      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch builder projects',
        data: []
      };
    }
  },

  // Listings an agent/agency posted from their own Add Listing screen (read-only for admin).
  // Backend limits: agent_id only returns approved listings; agency_id returns all approval states
  // but only rows tagged with that agency, so agencies also get their agent_id rows merged in.
  getListingsByUser: async (userId, role) => {
    try {
      const base = { browse: 'true', limit: 1000 };
      const queries = [apiGet('/api/properties', { ...base, agent_id: userId })];
      if (role === 'agency') queries.push(apiGet('/api/properties', { ...base, agency_id: userId }));
      const results = await Promise.all(queries);
      const byId = new Map();
      results.forEach((response) => {
        const rows = response?.data || response;
        (Array.isArray(rows) ? rows : []).forEach((row) => byId.set(row.id, row));
      });
      const data = [...byId.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return { success: true, data };
    } catch (error) {
      console.error('❌ Error fetching user listings:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch listings',
        data: []
      };
    }
  },

getProjectsByBuilderId: async (builderId) => {
    try {
      // builder_only: without it the backend adds "builder_id IS NULL" (agent listings) and returns nothing.
      // limit: backend defaults to 20 (newest first, all approval states) — request all so older approved projects aren't cut off
      const response = await apiGet('/api/properties', { builder_id: builderId, builder_only: 'true', browse: 'true', limit: 1000 });
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching builder projects:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch builder projects',
        data: []
      };
    }
  },
  /**
   * Get property by ID
   * @param {string} id - Property ID
   * @returns {Promise<Object>} Property details
   */
  getPropertyById: async (id) => {
    try {
      console.log('🔍 Fetching property by ID:', id);

      const response = await apiGet(`/api/properties/${id}`);

      console.group('📦 Property Details API Response');
      console.log('Full Result:', JSON.parse(JSON.stringify(response)));
      console.groupEnd();

      return response;
    } catch (error) {
      console.group('❌ Property Details Error');
      console.error('Full Error:', error);
      console.groupEnd();

      return {
        success: false,
        error: error.message || 'Failed to fetch property details',
        data: null
      };
    }
  },

  /**
   * Get project details by ID
   * @param {string} id - Project ID
   * @returns {Promise<Object>} Project details
   */
  getProjectDetails: async (id) => {
    try {
      console.group('🔍 Fetching Project Details');
      console.log('Project ID:', id);
      console.log('Auth Token:', localStorage.getItem('authToken'));
      console.log('Attempting to fetch from properties endpoint');

      if (!id) {
        console.error('No project ID provided');
        return {
          success: false,
          message: 'No project ID provided'
        };
      }

      const response = await apiGet(`properties/${id}`);

      console.log('📦 Project Details Full Response:', response);
      console.groupEnd();

      return response;
    } catch (error) {
      console.group('❌ Project Details Fetch Error');
      console.error('Error Details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      console.groupEnd();

      return {
        success: false,
        message: error.message || 'Failed to fetch project details',
        details: error
      };
    }
  },

  // Rest of the methods (getPropertiesByBuilder, getInterestedProperties, etc.) remain largely the same
  // Just replace hard-coded headers with createApiHeaders()

  /**
   * Upload image
   * @param {File} file - Image file
   * @returns {Promise<Object>} - Upload result with URL
   */
  uploadImage: async (file) => {
    try {
      if (!file) {
        console.warn('No file provided for upload');
        return { success: false, message: 'No file provided' };
      }

      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 25 * 1024 * 1024; // 25MB

      if (!allowedTypes.includes(file.type)) {
        return {
          success: false,
          message: `Unsupported file type. Allowed types: ${allowedTypes.join(', ')}`
        };
      }

      if (file.size > maxSize) {
        return {
          success: false,
          message: `File too large. Maximum size is 25MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
        };
      }

      const formData = new FormData();
      formData.append('image', file, file.name);

      console.log('📤 Uploading image:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      const response = await fetch('https://sandbox.rebax.in/api/upload/image', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Image Upload Error:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });

        return {
          success: false,
          message: errorText || 'Failed to upload image',
          status: response.status
        };
      }

      const data = await response.json();

      console.log('📥 Image Upload Response:', data);

      return {
        success: true,
        data: data.data || data,
        message: 'Image uploaded successfully'
      };
    } catch (error) {
      console.error('❌ Error uploading image:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      return {
        success: false,
        message: error.message || 'Failed to upload image',
        details: error
      };
    }
  },

  /**
   * Upload document (PDF)
   * @param {File} file - Document file
   * @returns {Promise<Object>} - Upload result with URL
   */
  uploadDocument: async (file) => {
    try {
      if (!file) {
        console.warn('No document provided for upload');
        return { success: false, message: 'No document provided' };
      }

      // Validate file type and size
      const allowedTypes = ['application/pdf'];
      const maxSize = 25 * 1024 * 1024; // 25MB

      if (!allowedTypes.includes(file.type)) {
        return {
          success: false,
          message: `Unsupported file type. Allowed types: ${allowedTypes.join(', ')}`
        };
      }

      if (file.size > maxSize) {
        return {
          success: false,
          message: `File too large. Maximum size is 25MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
        };
      }

      const formData = new FormData();
      formData.append('document', file, file.name);

      console.log('📤 Uploading document:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      const response = await fetch('https://sandbox.rebax.in/api/upload/document', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Document Upload Error:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });

        return {
          success: false,
          message: errorText || 'Failed to upload document',
          status: response.status
        };
      }

      const data = await response.json();

      console.log('📥 Document Upload Response:', data);

      return {
        success: true,
        data: data.data || data,
        message: 'Document uploaded successfully'
      };
    } catch (error) {
      console.error('❌ Error uploading document:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      return {
        success: false,
        message: error.message || 'Failed to upload document',
        details: error
      };
    }
  },

  // Remaining methods follow the same pattern of using createApiHeaders()
  updatePropertyStatus: async (id, status) => {
    try {
      console.log('Updating property status:', id, 'to', status);

      return await apiCall(`/api/properties/${id}`, {
        method: 'PUT',
        body: { status }
      });
    } catch (error) {
      console.error('Error updating property status:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  },

  /**
   * Create a new property
   * @param {Object} formData - Property details from form
   * @returns {Promise<Object>} Created property details
   */
  createProperty: async (formData) => {
    try {
      // Validate required fields using backend field names
      const requiredFields = [
        'property_title', 'property_type', 'community_type',
        'project_status', 'price', 'price_per_sqft', 'total_units',
        'total_floors', 'total_towers', 'city', 'state'
      ];

      const missingFields = requiredFields.filter(field =>
        !formData[field] ||
        (typeof formData[field] === 'string' && formData[field].trim() === '') ||
        (typeof formData[field] === 'number' && formData[field] === 0)
      );

      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);
        return {
          success: false,
          message: `Missing required fields: ${missingFields.join(', ')}`
        };
      }

      // Safely access nested properties
      const getBudgetMin = () => {
        try {
          return formData.budget?.min ||
            (formData.budget && formData.budget[0]?.min) ||
            '0';
        } catch (err) {
          console.warn('Error accessing budget min:', err);
          return '0';
        }
      };

      const getBudgetMax = () => {
        try {
          return formData.budget?.max ||
            (formData.budget && formData.budget[0]?.max) ||
            '0';
        } catch (err) {
          console.warn('Error accessing budget max:', err);
          return '0';
        }
      };

      // Use formData directly as it's already prepared in the correct format
      const payload = formData;

      console.log('🏗️ Creating Property - Normalized Payload:', payload);

      // Use fetch instead of apiCall for more detailed error handling
      const response = await fetch('https://sandbox.rebax.in/api/properties', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Property Creation Error:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });

        return {
          success: false,
          message: errorText || 'Failed to create property',
          status: response.status
        };
      }

      const data = await response.json();

      console.log('📦 Property Creation Response:', data);

      return {
        success: true,
        data: data.data || data,
        message: 'Property created successfully'
      };
    } catch (error) {
      console.error('❌ Error creating property:', {
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        formData: JSON.stringify(formData, null, 2)
      });

      return {
        success: false,
        message: error.message || 'Failed to create property',
        details: error
      };
    }
  },

  /**
   * Update an existing property
   * @param {string} propertyId - Property ID
   * @param {Object} propertyData - Updated property details
   * @returns {Promise<Object>} Updated property details
   */
  updateProperty: async (propertyId, propertyData) => {
    try {
      console.log('🔄 Updating Property:', { propertyId, propertyData });

      const response = await apiCall(`/api/properties/${propertyId}`, {
        method: 'PUT',
        body: propertyData
      });

      console.log('📦 Property Update Response:', response);

      return {
        success: true,
        data: response.data,
        message: 'Property updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating property:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update property',
        error: error.message
      };
    }
  },

  /**
   * Search properties with advanced filters
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Object>} - Search results
   */
  searchProperties: async (searchParams) => {
    // try {
    //   console.log('🔍 Searching properties with params:', searchParams);

    //   const response = await fetch('https://sandbox.rebax.in/api/properties/search', {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //       'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    //     },
    //     body: JSON.stringify(searchParams)
    //   });

    //   if (!response.ok) {
    //     const errorText = await response.text();
    //     console.error('❌ Property Search Error:', {
    //       status: response.status,
    //       statusText: response.statusText,
    //       errorText
    //     });

    //     return {
    //       success: false,
    //       message: errorText || 'Failed to search properties',
    //       status: response.status
    //     };
    //   }

    //   const data = await response.json();

    //   console.log('📦 Property Search Response:', data);

    //   return {
    //     success: true,
    //     data: data.data || data,
    //     message: 'Properties searched successfully'
    //   };
    // } catch (error) {
    //   console.error('❌ Error searching properties:', error);
    //   return {
    //     success: false,
    //     message: error.message || 'Failed to search properties',
    //     details: error
    //   };
    // }
    try {
      console.log('🔍 Fetching Sub Users');
      console.log('Auth Token:', localStorage.getItem('authToken'));
      const response = await apiPost('properties/search', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(searchParams)
      });
      console.log('Sub Users Response:', response);

      // Normalize user data
      const users = response.data.map(user => ({
        ...user,
        name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : user.first_name || user.last_name || user.email || 'No Name',
      }));

      return {
        success: true,
        data: users
      };
    } catch (error) {
      console.error('Error fetching sub-users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  },
    getProperties: async () => {
   
    try {
      console.log('🔍 Fetching Sub Users');
      console.log('Auth Token:', localStorage.getItem('authToken'));
      const response = await apiGet('properties', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify()
      });
      console.log('Sub Users Response:', response);

      // Normalize user data
      const users = response.data.map(user => ({
        ...user,
        name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : user.first_name || user.last_name || user.email || 'No Name',
      }));

      return {
        success: true,
        data: users
      };
    } catch (error) {
      console.error('Error fetching sub-users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  },

  /**
   * Get community type options for a given project type
   */
  getCommunityTypes: async (projectType) => {
    // Farm Lands reuses the Plots community type options
    const lookupType = projectType === 'Farm Lands' ? 'Plots' : projectType;
    try {
      const response = await apiGet(`properties/config/community-types?property_type=${encodeURIComponent(lookupType)}`);
      return response;
    } catch (error) {
      console.warn('Failed to fetch community types, using fallback:', error);
      const fallbackMap = {
        'Apartments': ['Gated Community', 'Semi-Gated Community', 'High-Rise Tower', 'Standalone Building'],
        'Villas': ['Gated Community', 'Semi-Gated Community', 'Villa Township', 'Standalone'],
        'Commercial': ['IT Park', 'Business Park', 'Mall/Retail Complex', 'Standalone Building'],
        'Plots': ['Gated Layout', 'HMDA Layout', 'DTCP Layout', 'Open Layout'],
      };
      return { success: true, data: fallbackMap[lookupType] || [] };
    }
  },

  /**
   * Toggle like/dislike vote for a property
   */
  toggleVote: async (propertyId, voteType) => {
    const response = await apiPost(`properties/votes/${propertyId}/vote`, { vote_type: voteType });
    return response;
  },

  /**
   * Get vote counts for a property
   */
  getVoteCounts: async (propertyId) => {
    const response = await apiGet(`properties/votes/${propertyId}/counts`);
    return response;
  },

  /**
   * Get current user's vote for a property
   */
  getUserVote: async (propertyId) => {
    const response = await apiGet(`properties/votes/${propertyId}/user-vote`);
    return response;
  },

  /**
   * Get approval projects for project verification - matching mobile app
   * @returns {Promise<Object>} List of projects pending approval
   */
  getApprovalProjects: async () => {
    try {
      console.log('🔍 Fetching approval projects for verification');

      const response = await apiGet('admin/approval-projects', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        }
      });

      console.log('Approval Projects Response:', response);

      return {
        success: true,
        data: response.data || response || []
      };
    } catch (error) {
      console.error('❌ Error fetching approval projects:', error);

      // Return empty array for now, can be expanded based on actual API endpoints
      return {
        success: true,
        data: []
      };
    }
  },


  // Get admin projects for approval (matching mobile app)
  getAdminProjects: async (params = {}) => {
    try {
      console.log('🔍 Fetching admin projects for verification');

      const response = await apiGet('admin/projects', {
        params: {
          page: params.page || 1,
          limit: params.limit || 1000,
          ...params
        },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        }
      });

      console.log('Admin Projects Response:', response);

      return {
        success: true,
        data: response.data || response || { projects: [] }
      };
    } catch (error) {
      console.error('Error fetching admin projects:', error);
      return {
        success: true,
        data: { projects: [] }
      };
    }
  },

  /**
   * Get approval statistics
   * @returns {Promise<Object>} Approval stats
   */
  getApprovalStats: async () => {
    try {
      console.log('📊 Fetching approval stats');
      const response = await apiGet('admin/projects/approval-stats', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        }
      });

      console.log('Approval Stats Response:', response);
      return response;
    } catch (error) {
      console.error('Error fetching approval stats:', error);
      return {
        success: true,
        data: {
          totalProjects: 0,
          pendingCount: 0,
          approvedCount: 0,
          rejectedCount: 0,
        }
      };
    }
  },

  /**
   * Approve a project
   * @param {number} projectId - Project ID to approve
   * @returns {Promise<Object>} Approval response
   */
  approveProject: async (projectId) => {
    try {
      console.log('✅ Approving project:', projectId);
      const response = await apiPost(`admin/properties/${projectId}/approve`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Approve project response:', response);
      return response;
    } catch (error) {
      console.error('Error approving project:', error);
      throw error;
    }
  },

  /**
   * Reject a project
   * @param {number} projectId - Project ID to reject
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Rejection response
   */
  rejectProject: async (projectId, reason) => {
    try {
      console.log('❌ Rejecting project:', projectId, 'with reason:', reason);
      const response = await apiPost(`admin/properties/${projectId}/reject`, {
        reason: reason
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Reject project response:', response);
      return response;
    } catch (error) {
      console.error('Error rejecting project:', error);
      throw error;
    }
  },

  /**
   * Get properties for approval - matching mobile app
   * @returns {Promise<Object>} List of properties pending approval
   */
  getPropertiesForApproval: async () => {
    try {
      console.log('🔍 Fetching properties for approval');
      const response = await apiGet('admin/properties/approval', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        }
      });

      console.log('Properties for approval response:', response);
      return {
        success: true,
        data: response.data || response || []
      };
    } catch (error) {
      console.error('Error fetching properties for approval:', error);
      return {
        success: true,
        data: []
      };
    }
  },
  claimProject: async (projectId) => {
    return await apiPost(`/api/admin/properties/${projectId}/claim`);
  },
}
export default propertyService;